import React, { CSSProperties } from "react";
import { useFormState } from "react-final-form";
import { ViewfinderCircleIcon, PaintBrushIcon } from "@heroicons/react/24/outline";
import { PrivacyCenterFontDto } from "../../../common/service/server/dto/PrivacyCenterDto";

export type ThemeFieldKey =
  | "activeColor"
  | "inactiveColor"
  | "backgroundColor"
  | "mainHeaderFontId"
  | "bodyFontId"
  | "navigationFontId"
  | "sectionHeaderFontId";

interface FormValues {
  activeColor: string;
  inactiveColor: string;
  backgroundColor: string;
  mainHeaderFontId: string;
  bodyFontId: string;
  navigationFontId: string;
  sectionHeaderFontId: string;
}

interface PrivacyCenterThemeVisualExampleProps {
  fonts: PrivacyCenterFontDto[];
  onHotspotClick: (fieldKey: ThemeFieldKey) => void;
}

const FALLBACK_PRIMARY = "#726D83";
const FALLBACK_SECONDARY = "#E8E5EE";
const FALLBACK_BACKGROUND = "#FFFFFF";

export const PrivacyCenterThemeVisualExample = ({
  fonts,
  onHotspotClick,
}: PrivacyCenterThemeVisualExampleProps) => {
  const { values } = useFormState<FormValues>();

  const fontNameById = (id: string | undefined) =>
    fonts?.find((f) => f.id === id)?.name || "Roboto";

  const activeColor = values?.activeColor || FALLBACK_PRIMARY;
  const inactiveColor = values?.inactiveColor || FALLBACK_SECONDARY;
  const backgroundColor = values?.backgroundColor || FALLBACK_BACKGROUND;
  const mainHeaderFont = fontNameById(values?.mainHeaderFontId);
  const bodyFont = fontNameById(values?.bodyFontId);
  const navigationFont = fontNameById(values?.navigationFontId);
  const sectionHeaderFont = fontNameById(values?.sectionHeaderFontId);

  const mockupStyle: CSSProperties = { backgroundColor };

  return (
    <div className="privacy-center-theme-visual-example" aria-hidden="true">
      <div className="privacy-center-theme-visual-example__frame">
        <img
          src="/assets/images/stay_compliant/privacy_center/visual_examples/chrome_browser_top_bar.png"
          alt=""
          className="privacy-center-theme-visual-example__browser-bar"
        />

        <div className="privacy-center-theme-visual-example__mockup" style={mockupStyle}>
          <div
            className="privacy-center-theme-visual-example__brand"
            style={{ fontFamily: "Merriweather, serif" }}
          >
            <span className="privacy-center-theme-visual-example__brand-acme">Acme </span>
            <span className="privacy-center-theme-visual-example__brand-inc">Inc.</span>
          </div>

          <div className="privacy-center-theme-visual-example__layout">
            <nav style={{ fontFamily: navigationFont }}>
              <div
                className="privacy-center-theme-visual-example__nav-item privacy-center-theme-visual-example__nav-item--active"
                style={{ backgroundColor: activeColor }}
              >
                Privacy Policy <span style={{ marginLeft: "auto" }}>›</span>
              </div>
              <div className="privacy-center-theme-visual-example__nav-item privacy-center-theme-visual-example__nav-item--inactive">
                US State Privacy Notices
              </div>
              <div className="privacy-center-theme-visual-example__nav-item privacy-center-theme-visual-example__nav-item--inactive">
                Make a Privacy Request
              </div>
              <div className="privacy-center-theme-visual-example__nav-item privacy-center-theme-visual-example__nav-item--inactive">
                Your Privacy Choices
              </div>
            </nav>

            <div>
              <h1
                className="privacy-center-theme-visual-example__main-header"
                style={{ fontFamily: mainHeaderFont }}
              >
                Our Privacy Policy
              </h1>
              <div className="privacy-center-theme-visual-example__last-updated">
                Last Updated: June 30, 2023
              </div>

              <div
                className="privacy-center-theme-visual-example__section-bar privacy-center-theme-visual-example__section-bar--primary"
                style={{ backgroundColor: activeColor, fontFamily: sectionHeaderFont }}
              >
                Introduction
              </div>
              <p
                className="privacy-center-theme-visual-example__body-text"
                style={{ fontFamily: bodyFont }}
              >
                We collect the personal information you provide to us when you make in-store
                purchases. The categories of information we may collect include:
              </p>
              <ul
                className="privacy-center-theme-visual-example__body-list"
                style={{ fontFamily: bodyFont }}
              >
                <li>
                  Personal Identifiers, including name, email address, postal address, and telephone
                  number
                </li>
                <li>Commercial and Financial Information, including purchases</li>
                <li>
                  Characteristics of Protected Classifications, including age and sex or gender
                </li>
                <li>Medical, including medical information</li>
              </ul>

              <div
                className="privacy-center-theme-visual-example__section-bar privacy-center-theme-visual-example__section-bar--secondary"
                style={{ backgroundColor: inactiveColor, fontFamily: sectionHeaderFont }}
              >
                Personal Data We Collect
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay hotspot buttons */}
      <button
        type="button"
        tabIndex={-1}
        className="privacy-center-theme-visual-example__hotspot"
        style={{ top: "30%", left: "64%" }}
        onClick={() => onHotspotClick("mainHeaderFontId")}
      >
        <ViewfinderCircleIcon className="privacy-center-theme-visual-example__hotspot-icon" />
        Header Font
      </button>

      <button
        type="button"
        tabIndex={-1}
        className="privacy-center-theme-visual-example__hotspot"
        style={{ top: "42%", left: "10%" }}
        onClick={() => onHotspotClick("navigationFontId")}
      >
        <ViewfinderCircleIcon className="privacy-center-theme-visual-example__hotspot-icon" />
        Navigation Font
      </button>

      <button
        type="button"
        tabIndex={-1}
        className="privacy-center-theme-visual-example__hotspot"
        style={{ top: "43%", left: "36%" }}
        onClick={() => onHotspotClick("sectionHeaderFontId")}
      >
        <ViewfinderCircleIcon className="privacy-center-theme-visual-example__hotspot-icon" />
        Section Header Font
      </button>

      <button
        type="button"
        tabIndex={-1}
        className="privacy-center-theme-visual-example__hotspot"
        style={{ top: "50%", right: "14%" }}
        onClick={() => onHotspotClick("activeColor")}
      >
        <PaintBrushIcon className="privacy-center-theme-visual-example__hotspot-icon" />
        Primary Color
        <span
          className="privacy-center-theme-visual-example__hotspot-swatch"
          style={{ backgroundColor: activeColor }}
        />
      </button>

      <button
        type="button"
        tabIndex={-1}
        className="privacy-center-theme-visual-example__hotspot"
        style={{ top: "70%", left: "15%" }}
        onClick={() => onHotspotClick("backgroundColor")}
      >
        <PaintBrushIcon className="privacy-center-theme-visual-example__hotspot-icon" />
        Background Color
        <span
          className="privacy-center-theme-visual-example__hotspot-swatch"
          style={{ backgroundColor }}
        />
      </button>

      <button
        type="button"
        tabIndex={-1}
        className="privacy-center-theme-visual-example__hotspot"
        style={{ bottom: "25%", left: "75%" }}
        onClick={() => onHotspotClick("bodyFontId")}
      >
        <ViewfinderCircleIcon className="privacy-center-theme-visual-example__hotspot-icon" />
        Body Font
      </button>

      <button
        type="button"
        tabIndex={-1}
        className="privacy-center-theme-visual-example__hotspot"
        style={{ bottom: "14%", right: "14%" }}
        onClick={() => onHotspotClick("inactiveColor")}
      >
        <PaintBrushIcon className="privacy-center-theme-visual-example__hotspot-icon" />
        Secondary Color
        <span
          className="privacy-center-theme-visual-example__hotspot-swatch"
          style={{ backgroundColor: inactiveColor }}
        />
      </button>
    </div>
  );
};
